import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { WholeState } from '../../store/common/type';
import {
  DDDeveloperListing,
  buildDDUrl,
  resolvePGImageUrl,
} from '../../lib/ddListingTypes';
import {
  UnitTypesWrapper,
  BedroomTabNav,
  BedroomTabItem,
  UnitTypeContainer,
  UnitSelectorDesktop,
  UnitSelectorButton,
  UnitButtonName,
  UnitButtonSize,
  UnitSelectorMobile,
  MobileUnitScroller,
  MobileUnitItem,
  MobileUnitName,
  MobileUnitSize,
  UnitDetailPanel,
  PriceRow,
  PriceLabel,
  NoPriceLabel,
  PriceBadge,
  SpecRow,
  SpecItem,
  FurnishingRow,
  FurnishingLabel,
  FurnishingValue,
  FeaturesSection,
  FeaturesLabel,
  FeaturesList,
  FeatureItem,
  FloorPlanWrapper,
  FloorPlanImage,
  FloorPlanExpandButtonFull,
  FloorPlanExpandButton,
  FloorPlanUnavailable,
  FloorPlanUnavailableText,
  RequestFloorPlanButton,
  LightboxOverlay,
  LightboxHeader,
  LightboxTitle,
  LightboxTitleText,
  LightboxSubText,
  LightboxCloseButton,
  LightboxBody,
  LightboxImage,
  LightboxNavButton,
  LightboxCounter,
  LastUpdatedText,
} from './UnitTypesFromDeveloper.style';
import {
  groupByBedrooms,
  getFloorPlans,
  getFloorSize,
  getBedroomDisplay,
  getListingIdentifier,
} from './utility/ddListingsHelpers';
// Icons
import { FloorPlanUnavailableIcon } from './assets/FloorPlanUnavailableIcon';
import { ChevronRightIcon } from './assets/ChevronRightIcon';
import { ExpandIcon } from './assets/ExpandIcon';
import { BedIcon } from './assets/BedIcon';
import { BathIcon } from './assets/BathIcon';
import { RulerIcon } from './assets/RulerIcon';
import { CurrencyIcon } from './assets/CurrencyIcon';
import { ExternalLinkIcon } from './assets/ExternalLinkIcon';
import { trackDDListingClick } from '../../common/analytics/analyticsTracker';
import { ADP } from '../../common/analytics/sourceTypes';
import { ANALYTICS_FEATURES } from '../../common/analytics/featureTypes';
import dateTimeHelper from '../../common/helpers/ui/DateTimeHelper';

interface Props {
  listing: DDDeveloperListing;
}

const UnitTypesFromDeveloper: React.FC<Props> = ({ listing }) => {
  const userInfo = useSelector((state: WholeState) =>
    state === undefined ? {} : state.common.user,
  );
  const listingIdentifier = getListingIdentifier(listing);

  const bedroomGroups = groupByBedrooms(listing.unitTypes ?? []);
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const [activeUnitIndex, setActiveUnitIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const activeGroup = bedroomGroups[activeGroupIndex];
  const activeUnit = activeGroup?.units[activeUnitIndex] ?? null;
  const floorPlans = activeUnit ? getFloorPlans(activeUnit, listing) : [];
  const lastUpdated = listing.dates?.contentUpdated?.date ?? null;

  const handleGroupChange = useCallback((idx: number) => {
    setActiveGroupIndex(idx);
    setActiveUnitIndex(0);
  }, []);

  const openLightbox = useCallback((idx: number) => {
    setLightboxIndex(idx);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  const prevFloorPlan = useCallback(() => {
    setLightboxIndex((i) => (i === 0 ? floorPlans.length - 1 : i - 1));
  }, [floorPlans.length]);

  const nextFloorPlan = useCallback(() => {
    setLightboxIndex((i) => (i === floorPlans.length - 1 ? 0 : i + 1));
  }, [floorPlans.length]);

  if (!bedroomGroups.length || !activeUnit) return null;

  const listingUrl =
    listing.urls?.listing?.desktopByLocales?.en ??
    listing.urls?.listing?.desktop;
  const unitHash = String(activeUnit.id);

  const furnishing = activeUnit.furnishing?.[0]?.description ?? null;
  const features = activeUnit.features ?? [];
  const priceType = activeUnit.priceType?.text ?? '';
  const priceText = activeUnit.minPrice?.text ?? null;
  const pricePerSqm = activeUnit.minPricePerArea?.[0]?.text ?? null;
  const bedroomsCount = activeUnit.configuration?.bedrooms?.value ?? null;
  const bedrooms = getBedroomDisplay(bedroomsCount);
  const bathrooms = activeUnit.configuration?.bathrooms?.value ?? null;
  const floorSize = getFloorSize(activeUnit);

  return (
    <UnitTypesWrapper>
      {/* Bedroom group tabs */}

      <BedroomTabNav>
        {bedroomGroups.map((group, idx) => (
          <BedroomTabItem
            key={group.bedroomCount}
            active={idx === activeGroupIndex}
            onClick={() => handleGroupChange(idx)}
          >
            {group.label}
          </BedroomTabItem>
        ))}
      </BedroomTabNav>

      <UnitTypeContainer>
        {/* Mobile unit selector */}
        <UnitSelectorMobile>
          <MobileUnitScroller>
            {activeGroup.units.map((unit, idx) => (
              <MobileUnitItem
                key={unit.unitTypeId}
                active={idx === activeUnitIndex}
                onClick={() => setActiveUnitIndex(idx)}
              >
                <MobileUnitName>{unit.name}</MobileUnitName>
                <MobileUnitSize>{getFloorSize(unit)}</MobileUnitSize>
              </MobileUnitItem>
            ))}
          </MobileUnitScroller>
        </UnitSelectorMobile>

        {/* Desktop unit selector */}
        <UnitSelectorDesktop>
          {activeGroup.units.map((unit, idx) => (
            <UnitSelectorButton
              key={unit.unitTypeId}
              active={idx === activeUnitIndex}
              onClick={() => setActiveUnitIndex(idx)}
            >
              <UnitButtonName>{unit.name}</UnitButtonName>
              <UnitButtonSize>{getFloorSize(unit)}</UnitButtonSize>
            </UnitSelectorButton>
          ))}
        </UnitSelectorDesktop>

        {/* Detail panel */}
        <UnitDetailPanel>
          {/* Price */}
          {priceText ? (
            <PriceRow>
              <PriceLabel>{priceText}</PriceLabel>
              {priceType && <PriceBadge>{priceType}</PriceBadge>}
            </PriceRow>
          ) : (
            <PriceRow>
              <NoPriceLabel>
                ยังไม่มีข้อมูลราคาในขณะนี้กดปุ่ม "ดูเพิ่มเติม & ติดต่อ" เพื่อขอราคา
              </NoPriceLabel>
            </PriceRow>
          )}

          {/* Specs */}
          <SpecRow>
            {bedrooms !== null && (
              <SpecItem>
                <span>{bedrooms}</span>
                <BedIcon />
              </SpecItem>
            )}
            {bathrooms !== null && (
              <SpecItem>
                <span>{bathrooms}</span>
                <BathIcon />
              </SpecItem>
            )}
            {floorSize && (
              <SpecItem>
                <span>{floorSize}</span>
                <RulerIcon />
              </SpecItem>
            )}
            {pricePerSqm && (
              <SpecItem>
                <span>{pricePerSqm}</span>
                <CurrencyIcon />
              </SpecItem>
            )}
          </SpecRow>

          {/* Furnishing */}
          {furnishing && (
            <FurnishingRow>
              <FurnishingLabel>ตกแต่งแล้ว:</FurnishingLabel>
              <FurnishingValue>{furnishing}</FurnishingValue>
            </FurnishingRow>
          )}

          {/* Features */}
          {features.length > 0 && (
            <FeaturesSection>
              <FeaturesLabel>องค์ประกอบต่าง ๆ ภายในยูนิต:</FeaturesLabel>
              <FeaturesList>
                {features.map((f) => (
                  <FeatureItem key={f.code}>{f.description}</FeatureItem>
                ))}
              </FeaturesList>
            </FeaturesSection>
          )}

          {/* Floor plan */}
          {floorPlans.length > 0 ? (
            <>
              <FloorPlanExpandButtonFull
                onClick={() => openLightbox(0)}
                aria-label="ขยายแปลนห้อง"
              >
                ดูแผนผังชั้น <ChevronRightIcon />
              </FloorPlanExpandButtonFull>
              <FloorPlanWrapper>
                <FloorPlanImage
                  src={resolvePGImageUrl(floorPlans[0].urlTemplate)}
                  alt="แปลนห้อง"
                  onClick={() => openLightbox(0)}
                  loading="lazy"
                />
                <FloorPlanExpandButton
                  onClick={() => openLightbox(0)}
                  aria-label="ขยายแปลนห้อง"
                >
                  <ExpandIcon />
                </FloorPlanExpandButton>
              </FloorPlanWrapper>
            </>
          ) : (
            <FloorPlanUnavailable>
              <FloorPlanUnavailableIcon />
              <FloorPlanUnavailableText>
                ยังไม่มีแผนผังชั้นให้ดูในตอนนี้
              </FloorPlanUnavailableText>
              <RequestFloorPlanButton
                href={buildDDUrl(listingUrl, unitHash)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackDDListingClick(
                    userInfo,
                    ADP.DD_LISTINGS,
                    ANALYTICS_FEATURES.DD_LISTINGS_DEVELOPER,
                    listingIdentifier,
                    `request-floorplan - ${activeUnit.name}`,
                  )
                }
              >
                ขอดูแผนผังชั้น <ExternalLinkIcon width={16} height={16} />
              </RequestFloorPlanButton>
            </FloorPlanUnavailable>
          )}
        </UnitDetailPanel>
      </UnitTypeContainer>

      {/* Floor plan lightbox */}
      {lightboxOpen && floorPlans.length > 0 && (
        <LightboxOverlay onClick={closeLightbox}>
          <LightboxHeader onClick={(e) => e.stopPropagation()}>
            <LightboxTitle>
              <LightboxTitleText>
                {listing.project?.metaByType?.verified?.name ??
                  listing.title?.en}
              </LightboxTitleText>
              {priceText && <LightboxSubText>{priceText}</LightboxSubText>}
            </LightboxTitle>
            <LightboxCloseButton onClick={closeLightbox} aria-label="Close">
              ✕
            </LightboxCloseButton>
          </LightboxHeader>

          <LightboxBody onClick={(e) => e.stopPropagation()}>
            {floorPlans.length > 1 && (
              <LightboxNavButton
                className="prev"
                onClick={prevFloorPlan}
                aria-label="Previous"
              >
                ‹
              </LightboxNavButton>
            )}
            <LightboxImage
              src={resolvePGImageUrl(floorPlans[lightboxIndex].urlTemplate)}
              alt={`แปลนห้อง ${lightboxIndex + 1}`}
              loading="lazy"
            />
            {floorPlans.length > 1 && (
              <LightboxNavButton
                className="next"
                onClick={nextFloorPlan}
                aria-label="Next"
              >
                ›
              </LightboxNavButton>
            )}
          </LightboxBody>

          {floorPlans.length > 1 && (
            <LightboxCounter>
              {lightboxIndex + 1}/{floorPlans.length}
            </LightboxCounter>
          )}
        </LightboxOverlay>
      )}
      {lastUpdated && (
        <LastUpdatedText>
          อัปเดตล่าสุด:{' '}
          {dateTimeHelper.getCustomDateTime(lastUpdated, 'D MMMM YYYY')}
        </LastUpdatedText>
      )}
    </UnitTypesWrapper>
  );
};

export default UnitTypesFromDeveloper;
